/** @type {any} */
const ShopifyAny = /** @type {any} */ (
  typeof Shopify !== "undefined" ? Shopify : undefined
);

/** @type {any} */
const ThemeAny = /** @type {any} */ (
  typeof Theme !== "undefined" ? Theme : undefined
);

import { DialogComponent } from "./dialog.js";
import { fetchConfig } from "./utilities-v2.js";
import { CartAddEvent, SellingPlanUpdateEvent } from "./events.js";
import { getCart, primeCartCache } from "./pyp-gift-manager.js";
// Pure line resolver: a cached `item.key` goes dead when another mutation
// (e.g. the BAWKDAY free-gift reconcile) re-keys the box line, so every write
// below re-resolves the parent from a fresh cart first.
import {
  findParentLine,
  isStaleLineError,
  isCartResponse,
} from "./cart-line-resolver.js";
// Bare `@theme/*` specifier (importmap) so the writes below serialize on the
// ONE shared queue with the free-gift reconciler / cart drawer (ACQ-247 D1).
import { cartOperationQueue } from "@theme/cart-operation-queue";
import { primeCart } from "@theme/storefront-stores";
// Same bare specifier as the cart drawer: a Recharge gift line is recognised by
// the recipient property the gift drawer attaches, not by handle or SKU.
import { isRechargeGiftLine } from "@theme/addons-skip-rule";

/**
 * Added To Cart drawer component.
 * Opens when a one-time product is added via product form.
 * Fetches the real PDP page to get gr-product-options with theme editor settings.
 *
 * @extends {DialogComponent}
 */
class AddedToCartDrawer extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Document>} */
  #cachedPages = new Map();

  /** @type {Map<string, Promise<Document | null>>} */
  #pendingFetches = new Map();

  /** @type {string | null} */
  #currentVariantId = null;

  /** @type {string | null} */
  #selectedSellingPlanId = null;

  /** @type {boolean} Product excluded from the free gift (GWP Eligible metafield false) */
  #gwpExcluded = false;

  /** @type {any | null} */
  #lineItem = null;

  /** @type {string | null} */
  #lineItemKey = null;

  /** @type {number} */
  #addedQuantity = 1;

  /** @type {string | null} */
  #addedSellingPlanId = null;

  /** @type {number} */
  #originalLineItemQuantity = 0;

  /** @type {HTMLElement | null} */
  #optionsElement = null;

  /** @type {boolean} */
  #isHandling = false;

  /** @type {boolean} */
  #isMounting = false;

  /** @type {Promise<void>} */
  #cartUpdateQueue = Promise.resolve();

  /** @type {Error | null} */
  #cartUpdateError = null;

  /** @type {boolean} True while the proceed CTA's cart write is in flight */
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    this.#hydrateText();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(
      SellingPlanUpdateEvent.eventName,
      this.#handleSellingPlanUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(
      SellingPlanUpdateEvent.eventName,
      this.#handleSellingPlanUpdate,
    );
    this.#abortController?.abort();
  }

  #hydrateText() {
    const headingEl = /** @type {HTMLElement | null} */ (this.refs.headingText);

    if (headingEl) {
      headingEl.textContent = this.dataset.heading || "Added to Cart";
    }

    this.#updateFooter();
  }

  /**
   * Opens the cart drawer directly (used for variant one-time purchases, gift boxes,
   * and subscription adds on products excluded from the free gift)
   */
  #openCartDrawer() {
    const cartDrawer = document.querySelector("gr-cart-drawer, cart-drawer");
    if (
      cartDrawer &&
      typeof (/** @type {any} */ (cartDrawer).open) === "function"
    ) {
      /** @type {any} */ (cartDrawer).open();
    }
  }

  /**
   * Fetches the product page content (with caching and deduplication)
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // Return cached version if available
    const cached = this.#cachedPages.get(productPageUrl);
    if (cached) return cached;

    // Return pending fetch if one is already in-flight
    const pending = this.#pendingFetches.get(productPageUrl);
    if (pending) return pending;

    // Create new fetch promise
    const fetchPromise = this.#doFetch(productPageUrl);
    this.#pendingFetches.set(productPageUrl, fetchPromise);

    try {
      const result = await fetchPromise;
      if (result) {
        this.#cachedPages.set(productPageUrl, result);
      }
      return result;
    } finally {
      this.#pendingFetches.delete(productPageUrl);
    }
  }

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #doFetch(productPageUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`,
        );
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, "text/html");
    } catch (error) {
      const err = /** @type {any} */ (error);
      if (err?.name === "AbortError") {
        return null;
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /** @param {CustomEvent} event */
  #handleCartAdd = async (event) => {
    const {
      source,
      sourceContext,
      didError,
      sellingPlanId,
      isAddon,
      variantId: dataVariantId,
      itemCount,
      cart: eventCart,
      addedItem,
      gwpExcluded,
    } = event.detail?.data || {};

    const variantId = dataVariantId || event.detail?.sourceId;

    if (source !== "product-form-component") return;
    if (sourceContext === "quick-view") return;
    if (didError) return;

    // Product-level free-gift exclusion (GWP Eligible metafield false),
    // refreshed from the CartAddEvent on every add.
    this.#gwpExcluded = gwpExcluded === true;

    // Skip drawer for subscription adds UNLESS it's from a product card
    // Product card subscription adds should still open the drawer
    // PDP main form subscription adds go directly to gift selection, except for
    // products excluded from the free gift, which open the cart drawer instead.
    const isFromProductCard = sourceContext === "product-card";
    if (sellingPlanId) {
      // Gift-excluded products have no gift step from any surface, so the
      // cart drawer opens directly (product card and PDP alike).
      if (gwpExcluded === true) {
        this.#openCartDrawer();
        return;
      }
      if (!isFromProductCard) return;
    }

    // Skip drawer for addon products (check event data)
    if (isAddon === true) return;

    // Also skip if we're on the addons collection page
    if (window.location.pathname.includes("/collections/pre-checkout-add-ons"))
      return;

    // Skip drawer for variant buy block one-time purchase - open cart drawer instead
    if (sourceContext === "variant-onetime") {
      this.#openCartDrawer();
      return;
    }

    if (!variantId) return;

    // Prevent duplicate handling
    if (this.#isHandling) return;
    this.#isHandling = true;

    // Capture the quantity and selling plan that was just added (from the form submission)
    this.#addedQuantity = itemCount || 1;
    // Store the selling plan ID from the form submission - this is the source of truth
    this.#addedSellingPlanId = sellingPlanId || null;

    try {
      // Pass the added item from /cart/add.js response directly
      // This avoids Shopify's eventual consistency issues with /cart.js
      await this.#processCartAdd(variantId, eventCart, addedItem);
    } finally {
      this.#isHandling = false;
    }
  };

  /**
   * Process the cart add event after validation
   * @param {string | number} variantId
   * @param {Object} [eventCart] - Cart data from the event (avoids stale cache)
   * @param {Object} [addedItem] - The item returned from /cart/add.js response
   */
  async #processCartAdd(variantId, eventCart, addedItem) {
    // Trust the /cart/add.js response when it carries a variant_id: that's
    // what actually landed in the cart. The form's `variantId` can drift from
    // the cart's variant when a third-party script swaps it at the
    // network layer, so always sync currentVariantId off addedItem when we
    // have it. Falling back to the dispatched variantId only when the response
    // doesn't include one.
    const effectiveVariantId = addedItem?.variant_id
      ? String(addedItem.variant_id)
      : variantId.toString();
    this.#currentVariantId = effectiveVariantId;

    if (addedItem && addedItem.variant_id) {
      this.#lineItem = { ...addedItem, line: 1 };
      this.#lineItemKey = addedItem.key || null;
      this.#originalLineItemQuantity = addedItem.quantity || 1;
    } else {
      // Fallback to fetching from cart (for backwards compatibility)
      const lineItemLoaded = await this.#populateLineItem(
        this.#currentVariantId,
        eventCart,
      );
      if (!lineItemLoaded || !this.#lineItem) {
        console.warn("Failed to load line item, drawer will not open");
        return;
      }
    }

    // Check if the product is a gift box - open cart drawer instead. A Recharge
    // gift purchase (RET-280) takes the same exit: the cart drawer sends a
    // gift-only cart straight to checkout, and the add-ons upsell does not apply
    // to a credit the recipient redeems later.
    const boxHandle = this.#lineItem.handle || "";
    const sku = this.#lineItem.sku || "";
    const isGiftBox = boxHandle.includes("gift-box") || sku.startsWith("GBOX-");
    const isBeefStickBox = sku === "BOX-BFSTX";
    const isRechargeGift = isRechargeGiftLine(this.#lineItem);
    if (isGiftBox || isBeefStickBox || isRechargeGift) {
      this.#openCartDrawer();
      return;
    }

    // Check if the product is an addon by checking product_type
    const productType = this.#lineItem.product_type?.toLowerCase();
    if (productType === "addon") return;

    // Render product info from line item
    this.#renderProductInfo();

    // Fetch product page and mount options
    const handle = this.#lineItem.handle;
    if (handle) {
      const html = await this.fetchProductPage(`/products/${handle}`);
      if (!html) {
        console.warn("Failed to fetch product page, drawer will not open");
        return;
      }

      // Check if the fetched page shows this is an addon
      if (this.#checkIfAddon(html)) return;

      await this.#mountProductOptions(html);
    }

    this.showDialog();
  }

  /**
   * @param {string} variantId
   * @param {Object} [eventCart] - Cart data from the event (avoids stale cache)
   * @returns {Promise<boolean>} Returns true if line item was successfully populated
   */
  async #populateLineItem(variantId, eventCart) {
    try {
      // Use cart from event if available (freshest data), otherwise fetch
      // This avoids race conditions with the cart cache deduplication window
      let cart = eventCart?.items ? eventCart : null;
      if (!cart) {
        cart = await getCart({ force: true });
      }
      if (cart) primeCartCache(cart);

      // Find all items matching this variant that are not PYP gifts or promo
      // free gifts (ACQ-299) — targeting a reconciler-owned gift line here
      // would let the subscription upsell convert a free line into a paid one.
      const matchingItems =
        cart?.items?.filter(
          /** @param {any} item */
          (item) =>
            String(item.variant_id) === String(variantId) &&
            !item.properties?._is_pyp_gift &&
            !item.properties?._free_gift,
        ) || [];

      // Get the LAST matching item (most recently added)
      // Items are ordered by time added, so last = most recent
      const match =
        matchingItems.length > 0
          ? matchingItems[matchingItems.length - 1]
          : null;

      if (match && cart?.items) {
        this.#lineItem = { ...match, line: cart.items.indexOf(match) + 1 };
        // Store the unique key for precise targeting in cart updates
        this.#lineItemKey = match.key || null;
        // Store the total quantity in cart for this line item
        this.#originalLineItemQuantity = match.quantity || 1;
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to load cart item:", error);
      return false;
    }
  }

  #renderProductInfo() {
    const item = this.#lineItem;
    if (!item) return;

    const imageEl = /** @type {HTMLImageElement | null} */ (
      this.refs.productImage
    );
    const titleEl = /** @type {HTMLElement | null} */ (this.refs.productTitle);
    const variantEl = /** @type {HTMLElement | null} */ (
      this.refs.variantTitle
    );
    const linkEl = /** @type {HTMLAnchorElement | null} */ (
      this.refs.productLink
    );

    if (imageEl && item.image) {
      imageEl.src = item.image;
      imageEl.alt = item.product_title || "Product image";
    }

    if (titleEl) {
      titleEl.textContent = item.product_title || "";
    }

    if (variantEl) {
      const variantTitle =
        item.variant_title && item.variant_title !== "Default Title"
          ? item.variant_title
          : "";
      const priceText =
        item.price != null ? this.#formatMoney(item.price, true) : "";

      if (variantTitle && priceText) {
        variantEl.textContent = `${variantTitle} | ${priceText}`;
      } else if (variantTitle) {
        variantEl.textContent = variantTitle;
      } else {
        variantEl.textContent = priceText;
      }
    }

    if (linkEl) {
      linkEl.href = item.url || "#";
    }
  }

  /**
   * @param {Document} html
   */
  /**
   * Check if the product page indicates this is an addon product
   * @param {Document} html
   * @returns {boolean}
   */
  #checkIfAddon(html) {
    // Check for data-is-addon attribute on product-form-component
    const productFormComponent = html.querySelector(
      "product-form-component[data-is-addon]",
    );
    if (productFormComponent) return true;

    // Check for data attribute on other form elements
    const productForm = html.querySelector('[data-is-addon="true"]');
    if (productForm) return true;

    return false;
  }

  /**
   * Update option styling classes based on selection state
   * @param {Element | null} el
   * @param {boolean} isSelected
   */
  #setOptionSelected(el, isSelected) {
    if (!el) return;
    el.classList.toggle("border", !isSelected);
    el.classList.toggle("border-[#D8CFBB]", !isSelected);
    el.classList.toggle("bg-white", !isSelected);
    el.classList.toggle("border-[2.5px]", isSelected);
    el.classList.toggle("border-black-warm", isSelected);
    el.classList.toggle("bg-[#BDB0934D]", isSelected);
  }

  /**
   * Apply subscription state to the cloned options element
   * @param {HTMLElement} clone
   * @param {string | null} sellingPlanId
   */
  #applySubscriptionState(clone, sellingPlanId) {
    const subscriptionOption = clone.querySelector(
      '[ref="subscriptionOption"]',
    );
    const oneTimeOption = clone.querySelector('[ref="oneTimeOption"]');
    const subscriptionIndicator = clone.querySelector(
      '[ref="subscriptionIndicator"]',
    );
    const oneTimeIndicator = clone.querySelector('[ref="oneTimeIndicator"]');

    subscriptionOption?.setAttribute("data-selected", "true");
    oneTimeOption?.setAttribute("data-selected", "false");
    this.#setOptionSelected(subscriptionOption, true);
    this.#setOptionSelected(oneTimeOption, false);
    subscriptionIndicator?.classList.remove("hidden");
    oneTimeIndicator?.classList.add("hidden");

    // Set the frequency dropdown to match the selling plan
    const freqSelect = clone.querySelector('[ref="frequencySelect"]');
    if (freqSelect instanceof HTMLSelectElement && sellingPlanId) {
      for (let i = 0; i < freqSelect.options.length; i++) {
        const option = freqSelect.options[i];
        if (option?.dataset?.sellingPlanId === sellingPlanId) {
          freqSelect.selectedIndex = i;
          break;
        }
      }
    }

    this.#selectedSellingPlanId = sellingPlanId;
  }

  /**
   * Apply one-time purchase state to the cloned options element
   * @param {HTMLElement} clone
   */
  #applyOneTimeState(clone) {
    const subscriptionOption = clone.querySelector(
      '[ref="subscriptionOption"]',
    );
    const oneTimeOption = clone.querySelector('[ref="oneTimeOption"]');
    const subscriptionIndicator = clone.querySelector(
      '[ref="subscriptionIndicator"]',
    );
    const oneTimeIndicator = clone.querySelector('[ref="oneTimeIndicator"]');

    subscriptionOption?.setAttribute("data-selected", "false");
    oneTimeOption?.setAttribute("data-selected", "true");
    this.#setOptionSelected(subscriptionOption, false);
    this.#setOptionSelected(oneTimeOption, true);
    subscriptionIndicator?.classList.add("hidden");
    oneTimeIndicator?.classList.remove("hidden");

    this.#selectedSellingPlanId = null;
  }

  /**
   * @param {Document} html
   */
  async #mountProductOptions(html) {
    const host = /** @type {HTMLElement | null} */ (this.refs.optionsHost);
    if (!host) return;

    // Set mounting flag to ignore events during initialization
    this.#isMounting = true;

    host.innerHTML = "";
    this.#optionsElement = null;

    const optionsEl = html.querySelector("gr-product-options");
    if (!optionsEl) return;

    const clone = /** @type {HTMLElement} */ (optionsEl.cloneNode(true));
    clone.id = "added-to-cart-product-options";
    this.#optionsElement = clone;

    // Use the selling plan ID from the form submission (the source of truth for what was added)
    // NOT the line item's selling_plan_allocation (which could be from a different item)
    const itemHasSubscription = Boolean(this.#addedSellingPlanId);
    const itemSellingPlanId = this.#addedSellingPlanId;

    // Set the correct DOM state BEFORE appending (before connectedCallback runs)
    // This ensures the component initializes with the correct state
    if (itemHasSubscription) {
      this.#applySubscriptionState(clone, itemSellingPlanId);
    } else {
      this.#applyOneTimeState(clone);
    }

    // Now append - connectedCallback will read the correct initial state from DOM
    host.appendChild(clone);

    // Wait for custom element to be defined and upgraded
    await customElements.whenDefined("gr-product-options");
    customElements.upgrade(clone);

    // Also upgrade any frequency modal inside
    const freqModal = clone.querySelector("gr-frequency-modal");
    if (freqModal) {
      await customElements.whenDefined("gr-frequency-modal");
      customElements.upgrade(freqModal);
    }

    // Give the component time to initialize
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Update footer to match the selection
    this.#updateFooter(itemHasSubscription);

    // Clear mounting flag - initialization complete
    this.#isMounting = false;
  }

  /**
   * @param {number | string} value
   * @param {boolean} [removeTrailingZeros=false] - Whether to remove trailing zeros (e.g., $10.00 -> $10)
   */
  #formatMoney(value, removeTrailingZeros = false) {
    const moneyFormat = ThemeAny?.moneyFormat || ShopifyAny?.money_format;
    let formatted;
    if (ShopifyAny?.formatMoney) {
      formatted = ShopifyAny.formatMoney(value, moneyFormat);
    } else {
      const numericValue =
        typeof value === "number" ? value / 100 : Number(value) / 100;
      formatted = `$${numericValue.toFixed(2)}`;
    }

    if (removeTrailingZeros) {
      formatted = formatted.replace(/\.0+$/, "");
    }

    return formatted;
  }

  /** @param {SellingPlanUpdateEvent} event */
  #handleSellingPlanUpdate = (event) => {
    if (!this.#optionsElement?.contains(/** @type {Node} */ (event.target)))
      return;

    if (this.#isMounting) return;

    const { sellingPlanId } = event.detail || {};
    this.#selectedSellingPlanId = sellingPlanId || null;
    this.#updateFooter(Boolean(sellingPlanId));

    // Chain the update to the queue.
    // This allows other methods to wait for this specific operation to complete.
    this.#cartUpdateQueue = this.#cartUpdateQueue
      .then(async () => {
        this.#cartUpdateError = null;

        if (this.#lineItem?.line) {
          await this.#applySellingPlanToLineItem(sellingPlanId || null);
        }
      })
      .catch((err) => {
        console.error("Background selling plan update failed:", err);
        this.#cartUpdateError = err;
      });
  };

  /**
   * @param {boolean} [isSubscription] - Whether subscription is selected. If undefined, uses #selectedSellingPlanId
   */
  #updateFooter(isSubscription) {
    const isSub =
      isSubscription !== undefined
        ? isSubscription
        : Boolean(this.#selectedSellingPlanId);
    const footerEl = /** @type {HTMLElement | null} */ (this.refs.footerText);
    const ctaEl = /** @type {HTMLElement | null} */ (this.refs.ctaText);

    if (footerEl) {
      footerEl.textContent = isSub
        ? this.dataset.nextSubscription || ""
        : this.dataset.nextOneTime || "";
    }

    if (ctaEl) {
      ctaEl.textContent = isSub
        ? this.dataset.ctaSubscription || ""
        : this.dataset.ctaOneTime || "";
    }
  }

  async handleProceed() {
    if (this.#busy) return;
    this.#setBusy(true);

    try {
      // Wait for any pending selling plan toggle to finish before proceeding.
      await this.#cartUpdateQueue;

      if (this.#cartUpdateError) {
        // The background toggle failed, so the cart line may not match the
        // selected option. Re-apply the selection once, in the foreground,
        // before deciding where to send the shopper.
        console.error("Cart update failed:", this.#cartUpdateError);
        this.#cartUpdateError = null;
        try {
          await this.#applySellingPlanToLineItem(this.#selectedSellingPlanId);
        } catch (error) {
          console.error("Failed to re-apply selling plan:", error);
          this.#showError();
          return;
        }
      }

      if (this.#selectedSellingPlanId && !this.#gwpExcluded) {
        await this.#upgradeToSubscription();
        return;
      }
      // One-time, or a subscription on a product excluded from the free gift: the
      // selling plan is already applied to the line, so continue to add-ons.
      this.#goToAddons();
    } finally {
      this.#setBusy(false);
    }
  }

  #goToAddons() {
    const addonsUrl =
      this.dataset.addonsUrl || "/collections/pre-checkout-add-ons";
    window.location.assign(addonsUrl);
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const button = /** @type {HTMLButtonElement | null} */ (
      this.refs.proceedButton
    );
    if (button) {
      button.disabled = busy;
      button.setAttribute("aria-busy", String(busy));
    }
  }

  /**
   * A cart write failed: keep the drawer open with the CTA enabled so the
   * shopper can retry, and say so in the footer. Never silently skip the gift
   * step by navigating away (the original failure mode of this drawer).
   */
  #showError() {
    const footerEl = /** @type {HTMLElement | null} */ (this.refs.footerText);
    if (footerEl) {
      footerEl.textContent =
        this.dataset.errorText ||
        "We couldn't update your cart. Please try again.";
    }
  }

  /**
   * Posts one /cart/change for the parent line, on the shared cart queue.
   *
   * The line is re-resolved from a FRESH cart immediately before the write
   * (by stamped `_line_item_id`, then cached key, then variant + current
   * selling plan), so a key that another mutation invalidated (e.g. the
   * BAWKDAY free-gift reconcile re-keying the box) is never posted blindly.
   * A stale-line rejection is retried ONCE after re-resolving; every other
   * failure throws so the caller can show an error instead of navigating.
   *
   * @param {(line: any) => Record<string, any>} buildChanges - Returns the
   *   `/cart/change` fields (selling_plan, properties) for the resolved line.
   * @returns {Promise<any>} The parent line as it exists after the write.
   */
  #changeParentLine(buildChanges) {
    const changeUrl = ThemeAny?.routes?.cart_change_url;
    if (!changeUrl) {
      return Promise.reject(
        new Error("Theme.routes.cart_change_url is not available"),
      );
    }

    return cartOperationQueue.enqueue(
      async (/** @type {number} */ sequence) => {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          const cart = await getCart({ force: true });
          const line = findParentLine(cart, {
            lineItemId: this.#lineItem?.properties?._line_item_id || null,
            key: this.#lineItemKey,
            variantId: this.#currentVariantId,
            // Match the plan the line currently carries (null = one-time);
            // the change below is what moves it to the selected plan.
            sellingPlanId:
              this.#lineItem?.selling_plan_allocation?.selling_plan?.id ??
              null,
          });
          if (!line) {
            throw new Error("Parent line not found in cart");
          }
          this.#adoptLine(line, cart);

          const changes = buildChanges(line);
          const body = { id: line.key, quantity: line.quantity, ...changes };

          const response = await fetch(
            changeUrl,
            fetchConfig("json", { body: JSON.stringify(body) }),
          );
          const result = await response.json().catch(() => null);

          if (response.ok && isCartResponse(result)) {
            // Through the store when registered, so it applies this write in
            // queue order alongside the cache (ACQ-559).
            primeCart(result, sequence);
            const updated = findParentLine(result, {
              lineItemId:
                changes.properties?._line_item_id ||
                line.properties?._line_item_id ||
                null,
              variantId: this.#currentVariantId,
              sellingPlanId:
                "selling_plan" in changes
                  ? (changes.selling_plan ?? null)
                  : (line.selling_plan_allocation?.selling_plan?.id ?? null),
            });
            if (!updated) {
              throw new Error("Updated line not found in cart response");
            }
            this.#adoptLine(updated, result);
            return updated;
          }

          if (attempt === 0 && isStaleLineError(response.status, result)) {
            // The key we just read was already dead (read-replica lag or a
            // write that landed between read and write). Resolve again, once.
            continue;
          }

          const detail =
            result?.errors || result?.description || result?.message || "";
          throw new Error(
            `Cart change failed (${response.status}) ${
              typeof detail === "string" ? detail : JSON.stringify(detail)
            }`,
          );
        }
        throw new Error("Cart change failed: line key kept changing");
      },
    );
  }

  /**
   * Refreshes the cached line reference from a cart payload.
   * @param {any} line
   * @param {any} cart
   */
  #adoptLine(line, cart) {
    const index = Array.isArray(cart?.items) ? cart.items.indexOf(line) : -1;
    this.#lineItem = { ...line, line: index >= 0 ? index + 1 : 1 };
    this.#lineItemKey = line.key || null;
  }

  /**
   * Apply a selling plan to the line item without redirecting.
   * Throws on failure so the caller's queue records the error.
   * @param {string | null} sellingPlanId - The selling plan ID to apply, or null to remove subscription
   */
  async #applySellingPlanToLineItem(sellingPlanId) {
    if (!this.#lineItemKey && !this.#lineItem?.line) {
      throw new Error("No cart line to update");
    }
    await this.#changeParentLine(() => ({
      // selling_plan: null removes the subscription.
      selling_plan: sellingPlanId || null,
    }));
  }

  async #upgradeToSubscription() {
    if (!this.#lineItemKey && !this.#lineItem?.line) {
      this.#showError();
      return;
    }

    // UUID for gift linking. Preserved when the line already carries one so
    // gifts linked to it are never orphaned by a re-add.
    const newLineItemId = crypto.randomUUID();
    /** @type {any} */
    let updatedItem;
    try {
      updatedItem = await this.#changeParentLine((line) => ({
        selling_plan: this.#selectedSellingPlanId,
        properties: {
          ...(line.properties || {}),
          _line_item_id: line.properties?._line_item_id || newLineItemId,
          _parent_product: "yes",
          _has_double_gwp: Theme?.features?.double_gwp_enabled
            ? "true"
            : "false",
        },
      }));
    } catch (error) {
      console.error("Failed to update selling plan:", error);
      this.#showError();
      return;
    }

    this.closeDialog();

    const giftDrawer = /** @type {any} */ (
      document.querySelector("gift-selection-drawer")
    );
    if (giftDrawer && typeof giftDrawer.showSelectionMode === "function") {
      giftDrawer.showSelectionMode({
        parentItemId: String(updatedItem.id),
        parentItemKey: updatedItem.key,
        parentUuid: updatedItem.properties?._line_item_id || newLineItemId,
        parentSellingPlanId:
          updatedItem.selling_plan_allocation?.selling_plan?.id ||
          this.#selectedSellingPlanId ||
          undefined,
        parentQuantity: updatedItem.quantity,
      });
    } else {
      // No gift drawer on this page: the cart drawer has its own claim-gift
      // CTA, so open it rather than skipping the gift step entirely.
      this.#openCartDrawer();
    }
  }
}

if (!customElements.get("added-to-cart-drawer")) {
  customElements.define("added-to-cart-drawer", AddedToCartDrawer);
}
